import type { CoachMessage, FileItem, Task } from './types'
import { stageDef } from './stages'
import type { ReviewDetail } from '@/components/reviews/types'
import type { RolloutExamples } from '@/components/reviews/ReviewRollouts'
import type { CanonicalEvent } from './runs'

// Browser-only teaching data. These IDs never refer to stored tasks or files.
const at = new Date().toISOString()
export const introductionExample: Task = {
  task_id: 'introduction-example', title: 'Prepare a measurement report',
  program: 'Python', programs: ['Python'], workflow_version: 2,
  prompt: 'Prepare a report from measurements.csv. Include all three sample lengths, their units, and the source file. Keep the original measurement precision.',
  brief: {
    what: 'Prepare a short report from three sample measurements.',
    requester: 'A colleague who must compare the samples before a follow-up study.',
    use: 'Use the report to select samples for the next study.',
    starting_point: 'measurements.csv contains samples A, B, and C. Their lengths are 12.4, 18.2, and 9.8 mm.',
    finished_state: 'A readable report with each sample label, length, unit, and data source.',
    judgment_decisions: 'Choose a layout that makes comparison easy. Keep the original precision.',
    rules: 'Use the supplied values. Do not change their units or precision.',
    implicit_expectations: 'Make the report understandable without opening the source program.',
    acceptable_variation: 'A table or labelled chart is acceptable if it includes all required facts.',
    common_failures: ['Missing a sample or unit.', 'Changing the source measurements.'],
    time_and_difficulty: 'About 20 minutes. Check the report against the source file.',
    open_questions: '',
  },
  rubric: [
    { criterion_id: 'R1', role: 'required', criterion: 'The report contains all three sample labels and their original lengths.', evidence: 'Compare each label and value with measurements.csv.' },
    { criterion_id: 'R2', role: 'required', criterion: 'The report states the length unit for every measurement.', evidence: 'Read the table headings, chart labels, or individual values.' },
    { criterion_id: 'R3', role: 'required', criterion: 'The report identifies measurements.csv as its data source.', evidence: 'Read the source statement in the report.' },
  ],
  stage: 0, stage_info: { stage: 0, label: 'Task draft', owner: 'sme' },
  stage_entered_at: at, created_at: at, updated_at: at,
  sme_id: 'example-expert', sme_email: 'Example expert', verify: false,
  submission: { notes: 'I checked each sample label, measurement, and unit against the source file.' },
}

export const introductionTasks: Task[] = ([
  [0, 'Prepare a measurement report'], [2, 'Summarise inspection results'],
  [1, 'Compare three design options'], [3, 'Prepare a test plan'],
  [4, 'Check a data summary'], [5, 'Create a project handover'],
] as const).map(([stage, title], i) => {
  const info = stageDef(stage)
  return { ...introductionExample, task_id: `introduction-example-${i}`, title, stage,
    stage_info: { stage, label: info.label, owner: info.owner } }
})

export const introductionFiles: FileItem[] = ([
  { filename: 'measurements.csv', kind: 'initial_state', content_type: 'text/csv', size: 84 },
  { filename: 'full-execution.webm', kind: 'video', content_type: 'video/webm', size: 32526 },
  { filename: 'measurement-report.pdf', kind: 'final_output', content_type: 'application/pdf', size: 48000 },
] as const).map((file, i) => ({ ...file, file_id: `example-file-${i}`,
  task_id: introductionExample.task_id, s3_key: '', status: 'uploaded', stage_uploaded: 2, created_at: at }))

export const introductionConversation: CoachMessage[] = ([
  { role: 'expert', text: 'My first check says “The report is clear.” Is that enough?' },
  { role: 'coach', text: 'The word “clear” can mean different things. Which facts must the reader find? How would you check each fact in the final file?' },
  { role: 'expert', text: 'I will check the sample values, units, and source as separate criteria. I will compare the values with the input file.' },
] as const).map((message, i) => ({ ...message, seq: String(i).padStart(6, '0'), at }))

export const introductionRecording = '/introduction/measurement-report.webm'

/** Sample report frames feed the same playback controls as stored rollout screenshots. */
function reportFrame(units: boolean, filled = true) {
  const rows = ['A|12.4', 'B|18.2', 'C|9.8'].map((row, i) => {
    const [sample, value] = row.split('|')
    return `<text x="110" y="${260 + i * 64}">${sample}</text><text x="460" y="${260 + i * 64}">${filled ? value + (units ? ' mm' : '') : '—'}</text>`
  }).join('')
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540" viewBox="0 0 960 540">
    <rect width="960" height="540" fill="#eff0f5"/><rect width="960" height="44" fill="#fff"/>
    <g font-family="Arial, sans-serif" fill="#292a36"><text x="24" y="28" font-size="16">measurement-report · Document preview</text>
    <rect x="70" y="70" width="820" height="444" rx="8" fill="#fff" stroke="#dddfe8"/>
    <text x="110" y="128" font-size="30" font-weight="600">Sample measurement report</text>
    <text x="110" y="168" font-size="17" fill="#6a6c7c">Compare the three sample lengths.</text>
    <path d="M110 226 H840 M110 290 H840 M110 354 H840 M110 418 H840" stroke="#e3e5ed"/>
    <g font-size="20"><text x="110" y="212" font-weight="600">Sample</text><text x="460" y="212" font-weight="600">Length${units ? ' (mm)' : ''}</text>${rows}</g>
    <text x="110" y="460" font-size="17" fill="#6a6c7c">Source: measurements.csv</text></g></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

const event = (sequence: number, units: boolean, filled = true): CanonicalEvent => ({
  sequence, event_at: at, status: 'completed',
  rationale: filled ? 'I will compare the report with the source file before I finish.' : 'I will prepare a table for the three samples.',
  proposed_actions: [], executed_actions: [{ type: 'inspect', target: 'measurement-report' }],
  token_total: { input: 0, output: 0, reasoning: 0 },
  screenshot: { key: '', url: reportFrame(units, filled), width: 960, height: 540, sha256: '' },
})

export const introductionRollouts: RolloutExamples = {
  selectedKey: 'example-run/example-job/example-attempt-2',
  experiments: [{ run_id: 'example-run', name: 'Task validation', model: 'Example model', revision: 'example',
    attempts: [1, 2].map(i => ({ job_id: 'example-job', attempt_id: `example-attempt-${i}`, classification: 'success',
      summary: { reward: 1, steps: 3, verifier: { status: 'complete', passed: true,
        criteria: introductionExample.rubric.map(c => ({ id: c.criterion_id, label: c.criterion, passed: true, score: 1, weight: 1, required_for_pass: true })) } } })) }],
  evidence: Object.fromEntries([1, 2].map(i => [`example-run/example-job/example-attempt-${i}`, {
    attempt: { job_id: 'example-job', attempt_id: `example-attempt-${i}`, classification: 'success',
      summary: { reward: 1, verifier: { criteria: introductionExample.rubric.map(c => ({ id: c.criterion_id, label: c.criterion, passed: true, score: 1, weight: 1, required_for_pass: true })) } } },
    events: [event(1, i === 1), event(2, i === 1), event(3, i === 1)], files: [],
  }])),
}

export function introductionReview(quality: boolean): ReviewDetail {
  return {
    round: { round_id: quality ? 'example-quality' : 'example-peer', task_id: introductionExample.task_id,
      kind: quality ? 'quality' : 'submission', status: 'open', version: 1, created_at: at,
      title: introductionExample.title, program: introductionExample.program, programs: introductionExample.programs,
      author_email: 'Example expert', peer_email: 'You' },
    submission: { task: { ...introductionExample, stage: quality ? 4 : 1 }, files: introductionFiles },
    recommendations: [], comments: [], is_current: true, can_recommend: true, can_decide: false, task_revision: 0,
  }
}

export const introductionPeerDraft = { verdict: 'ready' as const,
  feedback: 'The request is clear. The recording covers the full task. The final report includes all values, units, and the source.' }
export const introductionQualityDraft = { verdict: 'needs_changes' as const,
  feedback: 'Rollout 2, step 3: R2 should fail. The report has no length units, but the check passed. Correct the unit check and run the attempts again.' }
